import { computed, ref, type Ref } from 'vue'
import { axios } from '@/lib'
import { getSavedSearches } from '@/lib/settings'
import type { SavedSearch, SavedSearchesData } from '@/types/shared.types'
import { toast } from 'vue-sonner'

const MAX_SAVED_SEARCHES = Number.POSITIVE_INFINITY
const MAX_PASSIVE_SAVED_SEARCHES = 10

// Global reactive state for saved searches - initialize with settings data
const globalSavedSearches = ref<SavedSearch[]>(getSavedSearches())

async function updateSavedSearches(searches: SavedSearch[]): Promise<SavedSearch[]> {
  const payload: SavedSearchesData = {
    SavedSearches: {
      $type: 'SoftwareIDM.AccessPanel.Models.SavedSearches, SoftwareIDM.AccessPanelModel',
      Searches: searches,
    },
  }
  await axios.post('/api/settings/profile', payload)
  window.userSettings = {
    ...(window.userSettings || ({} as SavedSearchesData)),
    SavedSearches: payload.SavedSearches,
  }
  return searches
}

export function useSavedSearches() {
  // Always re-sync from settings when the composable is invoked to avoid
  // leaking state between different consumers and tests.
  globalSavedSearches.value = getSavedSearches()

  const isLoading = ref(false)
  const isSaving = ref(false)
  const isDeleting = ref(false)
  const isUpdating = ref(false)

  // All searches sorted by last used
  const sortedSearchesAll = computed(() => {
    return [...globalSavedSearches.value].sort((a, b) => {
      return new Date(b.Used).getTime() - new Date(a.Used).getTime()
    })
  })

  // Only searches marked Active are visible in UI
  const visibleSavedSearches = computed(() => {
    return sortedSearchesAll.value.filter((s) => s.Active !== false)
  })

  // Passive (non-active) searches sorted by last used
  const passiveSavedSearches = computed(() => {
    return sortedSearchesAll.value.filter((s) => s.Active === false)
  })

  const saveSearch = async (name: string, term: string, silos: string[] = []) => {
    isSaving.value = true
    try {
      const now = new Date().toISOString()
      const newSearch: SavedSearch = {
        Name: name,
        Term: term,
        Silos: silos.length === 0 ? null : silos,
        Saved: now,
        Used: now,
        $type: 'SoftwareIDM.AccessPanel.Models.SavedSearch, SoftwareIDM.AccessPanelModel',
        Active: true,
      }

      let searches = [...globalSavedSearches.value]
      searches.push(newSearch)

      // Prune oldest searches if exceeding limit
      if (searches.length > MAX_SAVED_SEARCHES) {
        searches.sort((a, b) => new Date(b.Used).getTime() - new Date(a.Used).getTime())
        searches = searches.slice(0, MAX_SAVED_SEARCHES)
      }

      await updateSavedSearches(searches)
      globalSavedSearches.value = searches

      // Show success toast
      toast.success('Search saved successfully')
    } catch (err) {
      console.error('Error saving search:', err)
      toast.error('Failed to save search')
      // Don't re-throw to prevent additional error handling in components
    } finally {
      isSaving.value = false
    }
  }

  const deleteSearch = async (index: number) => {
    isDeleting.value = true

    // Get the search from the visible (active) sorted array
    const visibleList = visibleSavedSearches.value
    const searchToDelete = visibleList[index]

    if (!searchToDelete) {
      toast.error('Search not found')
      isDeleting.value = false
      return
    }

    // Find the actual index in the unsorted global array
    const actualIndex = globalSavedSearches.value.findIndex(
      (s) =>
        s.Name === searchToDelete.Name &&
        s.Term === searchToDelete.Term &&
        s.Saved === searchToDelete.Saved,
    )

    if (actualIndex === -1) {
      toast.error('Search not found')
      isDeleting.value = false
      return
    }

    // Store original state for rollback
    const originalSearches = [...globalSavedSearches.value]

    // Create a new array without the deleted item
    const searches = [...globalSavedSearches.value]
    searches.splice(actualIndex, 1)

    // Update optimistically first
    globalSavedSearches.value = searches

    // Show success toast immediately (optimistic) only for active searches
    if (searchToDelete.Active !== false) {
      toast.success('Search deleted successfully')
    }

    try {
      // Then persist to server
      await updateSavedSearches(searches)
    } catch (err) {
      console.error('Error deleting search:', err)
      // Rollback on failure
      globalSavedSearches.value = originalSearches
      toast.error('Failed to delete search')
    } finally {
      isDeleting.value = false
    }
  }

  // Delete a saved search by value (works for both active and passive)
  const deleteSearchByItem = async (search: SavedSearch) => {
    isDeleting.value = true

    if (!search) {
      toast.error('Search not found')
      isDeleting.value = false
      return
    }

    const actualIndex = globalSavedSearches.value.findIndex(
      (s) => s.Name === search.Name && s.Term === search.Term && s.Saved === search.Saved,
    )

    if (actualIndex === -1) {
      toast.error('Search not found')
      isDeleting.value = false
      return
    }

    const originalSearches = [...globalSavedSearches.value]
    const searches = [...globalSavedSearches.value]
    searches.splice(actualIndex, 1)

    // Optimistic update
    globalSavedSearches.value = searches
    // Only show success toast for active searches; suppress for passive
    if (search.Active !== false) {
      toast.success('Search deleted successfully')
    }

    try {
      await updateSavedSearches(searches)
    } catch (err) {
      console.error('Error deleting search:', err)
      globalSavedSearches.value = originalSearches
      toast.error('Failed to delete search')
    } finally {
      isDeleting.value = false
    }
  }

  const updateSearchUsedTime = async (index: number) => {
    isUpdating.value = true
    try {
      // Resolve selected search by index from visible list and update matching item in global list
      const visibleList = visibleSavedSearches.value
      const selected = visibleList[index]
      if (!selected) return

      const searches = [...globalSavedSearches.value]
      const actualIndex = searches.findIndex(
        (s) => s.Name === selected.Name && s.Term === selected.Term && s.Saved === selected.Saved,
      )
      if (actualIndex !== -1) {
        searches[actualIndex].Used = new Date().toISOString()
        await updateSavedSearches(searches)
        globalSavedSearches.value = searches
      }
    } catch (err) {
      console.error('Error updating search:', err)
      throw err
    } finally {
      isUpdating.value = false
    }
  }

  // Update the last-used timestamp using a specific item (safe with filtered views)
  const updateSearchUsedTimeByItem = async (search: SavedSearch) => {
    isUpdating.value = true
    try {
      if (!search) return

      const searches = [...globalSavedSearches.value]
      const actualIndex = searches.findIndex(
        (s) => s.Name === search.Name && s.Term === search.Term && s.Saved === search.Saved,
      )
      if (actualIndex !== -1) {
        searches[actualIndex].Used = new Date().toISOString()
        await updateSavedSearches(searches)
        globalSavedSearches.value = searches
      }
    } catch (err) {
      console.error('Error updating search:', err)
      throw err
    } finally {
      isUpdating.value = false
    }
  }

  // Create a computed filtered list bound to a provided query ref (non-global)
  const createFilteredSavedSearches = (query: Ref<string>) =>
    computed(() => {
      const q = query.value.trim().toLowerCase()
      if (!q) return visibleSavedSearches.value
      return visibleSavedSearches.value.filter((s) => {
        const name = (s.Name || '').toLowerCase()
        const term = (s.Term || '').toLowerCase()
        return name.includes(q) || term.includes(q)
      })
    })

  // Passive save: upsert a non-active search entry when a user performs a search
  const savePassiveSearch = async (term: string, silos: string[] = []) => {
    if (!term && silos.length === 0) return
    try {
      const now = new Date().toISOString()
      const searches = [...globalSavedSearches.value]

      // Try to find an existing matching search by term and silos
      const matchIndex = searches.findIndex(
        (s) =>
          s.Term === term &&
          JSON.stringify(s.Silos || null) === JSON.stringify(silos.length === 0 ? null : silos),
      )

      let addedNewPassive = false
      if (matchIndex !== -1) {
        // Update last used; preserve existing Active flag
        searches[matchIndex].Used = now
      } else {
        // Create a passive entry (hidden from UI)
        const passive: SavedSearch = {
          Name: term || 'Search',
          Term: term,
          Silos: silos.length === 0 ? null : silos,
          Saved: now,
          Used: now,
          $type: 'SoftwareIDM.AccessPanel.Models.SavedSearch, SoftwareIDM.AccessPanelModel',
          Active: false,
        }
        searches.push(passive)
        addedNewPassive = true
      }

      // Enforce passive-only limit by removing the oldest passives until capped
      if (addedNewPassive) {
        const passiveWithIndex = searches
          .map((s, i) => ({ s, i }))
          .filter(({ s }) => s.Active === false)
          .sort((a, b) => new Date(a.s.Used).getTime() - new Date(b.s.Used).getTime())

        if (passiveWithIndex.length > MAX_PASSIVE_SAVED_SEARCHES) {
          const toRemoveCount = passiveWithIndex.length - MAX_PASSIVE_SAVED_SEARCHES
          const indicesToRemove = passiveWithIndex
            .slice(0, toRemoveCount)
            .map(({ i }) => i)
            .sort((a, b) => b - a) // remove from highest index first to avoid reindexing issues

          for (const idx of indicesToRemove) {
            searches.splice(idx, 1)
          }
        }
      }

      await updateSavedSearches(searches)
      globalSavedSearches.value = searches
    } catch (err) {
      console.error('Error passively saving search:', err)
    }
  }

  return {
    savedSearches: visibleSavedSearches,
    passiveSavedSearches,
    isLoading,
    saveSearch,
    savePassiveSearch,
    deleteSearch,
    deleteSearchByItem,
    updateSearchUsedTime,
    updateSearchUsedTimeByItem,
    createFilteredSavedSearches,
    MAX_SAVED_SEARCHES,
    MAX_PASSIVE_SAVED_SEARCHES,
    isSaving,
    isDeleting,
    isUpdating,
  }
}
